export function createQualityDetailSaveService(deps) {
    const {
        entrydetailRef,
        selectedQualityContextRef,
        PANEL_TYPE,
        ROW_ID_FIELD,
        isEmpty,
        isEnabled,
        DicVal,
        canonicalPanelType,
        normalizeKakousijiUiString,
        normalizeFukushizaiContext,
        normalizeItemCd,
        normalizeGroupNo,
        normalizeColorCd,
        getQualityPanelFieldMap,
        qualityPanelContentColumnIds,
        readRowValueIgnoreCase,
        readCurrentFukushizaiNo,
        ensureDetailRowsByTypeForMutation,
        getDetailRowsByPanelType,
        qualityTargetColorsForContext,
        readQualityDisplayTargetColors,
        isQualityDisplayRowMeaningful,
        readFukushizaiItemGroupFromAnyRow,
        rowMatchesFukushizaiItemGroup,
        setRowContextForPanel,
        nextDetailRowIdForRows,
        normalizeQualityDisplayRowForSave,
        isInvalidDisplayRow,
        isQualityPanelDirty
    } = deps;

    // 表示順が空のときの補完値を返します。
    //
    // 表示順はDBの数値列のため、行IDをそのまま入れてはいけません。
    // Grid が採番する行IDは "GID17_123_1i40umu727o" のような文字列で、
    // 数値列へ渡すと列名として解釈され SQL エラーになります。
    function resolveFallbackDisplayOrder(row) {
        const rowId = normalizeKakousijiUiString(DicVal(row, ROW_ID_FIELD, ""));
        return /^\d+$/.test(rowId) ? rowId : "1";
    }

    // 品質表示 row を保存用 detail 形式へ正規化します。
    function normalizeQualityRowForSave(row, panelType = "") {
        const map = getQualityPanelFieldMap(panelType);
        if (isEmpty(map) || isEmpty(row) || typeof row !== "object") return row;

        const out = { ...row };
        const group = normalizeGroupNo(readRowValueIgnoreCase(out, map.group));
        const fallbackGroup = normalizeGroupNo(readRowValueIgnoreCase(out, map.groupFallback));

        // group が空で fallback に値がある場合は保存先 group へ寄せます。
        if (group === "" && fallbackGroup !== "") out[map.group] = fallbackGroup;
        if (!isEmpty(DicVal(map, "groupFallback", ""))) delete out[map.groupFallback];

        const orderField = String(DicVal(map, "order", "") ?? "").trim();
        if (orderField !== "" && normalizeKakousijiUiString(readRowValueIgnoreCase(out, orderField)) === "") {
            out[orderField] = resolveFallbackDisplayOrder(out);
        }

        return out;
    }

    // 品質表示系の各 type を保存用 detail 行へ変換します。
    function toQualitySaveRows(rows, panelType, contextOverride = null) {
        const type = canonicalPanelType(panelType);
        const map = getQualityPanelFieldMap(type);
        if (isEmpty(map)) return Array.isArray(rows) ? rows : [];

        // 品質表示は副資材と独立した選択のため、既定は品質側 context を使う。
        const context = normalizeFukushizaiContext(contextOverride || selectedQualityContextRef.current);
        const detail = ensureDetailRowsByTypeForMutation(type);
        // 論理削除済み(INVALID=1)の行は保存 payload へ載せない(送らない=物理削除)。
        // サーバ保存は「全削除→送信行の再INSERT」であり、送ると INVALID 列が
        // 保存可能列セットから strip され、サーバの既存行マージで INVALID=0 に戻って
        // 削除したグループが復活する。早期 return (existingRows) の経路も含めて
        // 取得時点で除外しておく。
        const existingRows = (Array.isArray(detail.rows) ? detail.rows : [])
            .filter((row) => !isInvalidDisplayRow(row, type));

        // item/group が無い場合は現状 detail を維持します。
        if (context.itemCd === "" || context.groupNo === "") return existingRows;

        const displayRows = Array.isArray(rows) ? rows : [];
        const baseTargetColors = qualityTargetColorsForContext(context, existingRows, type);
        const metaTargetColors = displayRows.flatMap((row) => readQualityDisplayTargetColors(row));
        const targetColors = [...new Set(
            [...baseTargetColors, ...metaTargetColors]
                .map((value) => normalizeColorCd(value))
                .filter((value) => value !== "")
        )];

        const saveDisplayRows = displayRows.filter((row) => isQualityDisplayRowMeaningful(row, type));
        const allRowsAreEmptyPlaceholders =
            displayRows.length !== 0 &&
            displayRows.every((row) => isEnabled(DicVal(row, "__QUALITY_EMPTY_INPUT_ROW", false)));

        const preservedRows = existingRows
            .filter((row) => {
                const rowContext = readFukushizaiItemGroupFromAnyRow(row);
                if (rowContext.itemCd === "" || rowContext.groupNo === "" || rowContext.colorCd === "") return false;
                return !rowMatchesFukushizaiItemGroup(row, context.itemCd, context.groupNo);
            })
            .map((row) => {
                const rowContext = readFukushizaiItemGroupFromAnyRow(row);
                const next = { ...row };
                setRowContextForPanel(next, type, rowContext.itemCd, rowContext.groupNo, rowContext.colorCd);
                return next;
            });

        // ユーザーが当該パネルを編集した(dirty)うえでの空表示は「全行削除」の確定として扱います。
        // サーバ保存は送信内容で全置換するため、現 context の行を空で確定しないと
        // 「全行削除して保存」がDBへ反映されず、再表示で行が復活します。
        // dirty でない場合(グリッド未初期化など)は従来どおり現状維持し、誤全消しを防ぎます。
        const panelDirty = typeof isQualityPanelDirty === "function" && isQualityPanelDirty(type, context);
        const commitEmptyRows = () => {
            entrydetailRef.current[detail.key] = preservedRows;
            return preservedRows;
        };
        if (displayRows.length === 0) {
            return panelDirty ? commitEmptyRows() : existingRows;
        }
        // 対象 color が解決できない場合は現状維持にします。
        if (targetColors.length === 0) return existingRows;
        if (saveDisplayRows.length === 0 && allRowsAreEmptyPlaceholders) {
            return panelDirty ? commitEmptyRows() : existingRows;
        }

        let nextRowId = nextDetailRowIdForRows(preservedRows);
        const outRows = [...preservedRows];

        saveDisplayRows.forEach((row, rowIndex) => {
            const cleanRow = normalizeQualityDisplayRowForSave(row, type);
            const orderField = String(DicVal(map, "order", "") ?? "").trim();

            targetColors.forEach((colorCd) => {
                const next = { ...cleanRow };
                next[ROW_ID_FIELD] = String(nextRowId++);

                // 表示順は常に表示順序で振り直します。
                //
                // 表示順は主キーの一部（例：D_SSDQ は 加工指示番号＋アイテム＋表示順＋グループ＋カラー）で、
                // 同一コンテキスト内で一意である必要があります。
                // 「空のときだけ補完」にすると、DBから読み込んだ既存行は元の番号を保持したまま
                // 追加行だけが表示位置で採番されるため、両者が同じ番号になり得ます。
                // その状態で保存すると明細の統合時に同一キーとして後勝ちでマージされ、片方が消えます。
                // （例：1つの組成にサブ組成を2件入力すると後の1件しか登録されない）
                if (orderField !== "") {
                    next[orderField] = String(rowIndex + 1);
                }

                setRowContextForPanel(next, type, context.itemCd, context.groupNo, colorCd);
                outRows.push(next);
            });
        });

        entrydetailRef.current[detail.key] = outRows;
        return outRows;
    }

    // 品質表示保存行の一意キーを返します。
    function qualitySaveKey(row, panelType) {
        const type = canonicalPanelType(panelType);
        const map = getQualityPanelFieldMap(type);
        if (isEmpty(map) || isEmpty(row)) return "";

        const no = normalizeKakousijiUiString(readRowValueIgnoreCase(row, map.no) || readCurrentFukushizaiNo());
        const item = normalizeItemCd(readRowValueIgnoreCase(row, map.item));
        const group = normalizeGroupNo(readRowValueIgnoreCase(row, map.group) ?? readRowValueIgnoreCase(row, map.groupFallback));
        const color = normalizeColorCd(readRowValueIgnoreCase(row, map.color));
        const order = normalizeKakousijiUiString(readRowValueIgnoreCase(row, map.order));
        if (item === "" || group === "" || color === "") return "";

        return `${no}|${item}|${group}|${color}|${order}`;
    }

    // 組成明細(119)に現存する行のキー集合を返します。
    //
    // 118(品質表示ベース)と119(組成明細)は同じ D_SSDQ を共有し、サーバ側で同一キーとして統合されます。
    // 118 は表示グリッドを持たないため、組成の行を削除しても detail から消えません。
    // 残ったままにすると、削除した行が 118 側の行として送信され、統合後も生き残ってしまいます。
    // （例：組成を削除しても混率の合計が減らない）
    function buildQualityCompositionKeySet() {
        const keys = new Set();
        const contexts = new Set();
        getDetailRowsByPanelType(entrydetailRef.current, PANEL_TYPE.QUALITY_DISPLAY_EXTRA).forEach((row) => {
            if (isEmpty(row) || isInvalidDisplayRow(row, PANEL_TYPE.QUALITY_DISPLAY_EXTRA)) return;
            const key = qualitySaveKey(row, PANEL_TYPE.QUALITY_DISPLAY_EXTRA);
            if (key === "") return;
            keys.add(key);
            // キーから表示順を除いた部分＝アイテム/グループ/カラー。
            contexts.add(key.substring(0, key.lastIndexOf("|")));
        });
        return { keys, contexts };
    }

    // 組成明細(119)がグリッド管理しているコンテキスト(無効化済み行も含む)を返します。
    // ここに含まれるコンテキストの組成データの実体は 119 側のペイロードであり、
    // 118(品質表示BASE)行に残った組成フィールドは古い写しにすぎません。
    function buildQualityCompositionManagedContexts() {
        const contexts = new Set();
        getDetailRowsByPanelType(entrydetailRef.current, PANEL_TYPE.QUALITY_DISPLAY_EXTRA).forEach((row) => {
            if (isEmpty(row)) return;
            const context = readFukushizaiItemGroupFromAnyRow(row);
            if (context.itemCd === "" || context.groupNo === "" || context.colorCd === "") return;
            contexts.add(`${context.itemCd}|${context.groupNo}|${context.colorCd}`);
        });
        return contexts;
    }

    // 118保存行から組成明細フィールド(+表示補助)を除去します。
    function stripCompositionFieldsFromBaseSaveRow(row) {
        const contentIds = typeof qualityPanelContentColumnIds === "function"
            ? qualityPanelContentColumnIds(PANEL_TYPE.QUALITY_DISPLAY_EXTRA)
            : [];
        contentIds.forEach((fieldId) => {
            const cid = String(fieldId ?? "").trim();
            if (cid === "") return;
            [cid, `${cid}_nm`, `${cid}_NM`, `${cid}_cd`, `${cid}_CD`].forEach((key) => {
                delete row[key];
            });
        });
        return row;
    }

    // 品質表示BASE項目から D_SSDQ 保存行を補完します。
    function buildQualityDssdqSaveRows() {
        const map = getQualityPanelFieldMap(PANEL_TYPE.QUALITY_BASE);
        const sourceRows = getDetailRowsByPanelType(entrydetailRef.current, PANEL_TYPE.QUALITY_BASE);
        const composition = buildQualityCompositionKeySet();
        const managedContexts = buildQualityCompositionManagedContexts();
        const merged = new Map();
        let rowid = 0;

        sourceRows.forEach((row) => {
            if (isEmpty(row) || isInvalidDisplayRow(row, PANEL_TYPE.QUALITY_BASE)) return;

            const normalized = normalizeQualityRowForSave(row, PANEL_TYPE.QUALITY_BASE);
            if (normalizeKakousijiUiString(readRowValueIgnoreCase(normalized, map.no)) === "") normalized[map.no] = readCurrentFukushizaiNo();
            if (normalizeKakousijiUiString(readRowValueIgnoreCase(normalized, map.order)) === "") normalized[map.order] = "1";

            const key = qualitySaveKey(normalized, PANEL_TYPE.QUALITY_BASE);
            if (key === "") return;

            // 組成明細が存在するコンテキストでは、そこに残っている行だけを対象にする。
            // 組成明細が1件も無いコンテキスト（品質表示ベースだけを使う運用）は従来どおり全件通す。
            const contextKey = key.substring(0, key.lastIndexOf("|"));
            if (composition.contexts.has(contextKey) && !composition.keys.has(key)) return;

            // 組成明細(119)がグリッド管理しているコンテキスト(削除済み含む)では、
            // 118行に残った組成フィールドの古い写しを保存へ流さない。
            // 流すと、119側で削除したコンテキストの組成が118経由で単独送信され、
            // 混率検証が部分的な組成セットを検出してしまう。
            const rowContext = readFukushizaiItemGroupFromAnyRow(normalized);
            if (managedContexts.has(`${rowContext.itemCd}|${rowContext.groupNo}|${rowContext.colorCd}`)) {
                stripCompositionFieldsFromBaseSaveRow(normalized);
            }

            const current = merged.get(key) || { [ROW_ID_FIELD]: String(rowid++) };
            const next = { ...current, ...normalized };
            merged.set(key, next);
        });

        return [...merged.values()];
    }

    return {
        normalizeQualityRowForSave,
        toQualitySaveRows,
        qualitySaveKey,
        buildQualityDssdqSaveRows
    };
}
